import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";
import { ShieldAlert } from "lucide-react";

/**
 * Forced password change page — shown immediately after a user logs in with
 * a temporary password (must_change_password = true). User cannot navigate
 * elsewhere until they set a new password.
 */
export default function ForcedChangePasswordPage() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw1.length < 6) return setError("New password must be at least 6 characters.");
    if (pw1 !== pw2) return setError("Passwords do not match.");
    if (pw1 === current) return setError("New password must differ from your temporary password.");
    setSubmitting(true);
    try {
      await api.post("/me/change-password", { current_password: current, new_password: pw1 });
      toast.success("Password updated. Welcome aboard!");
      await refresh();
      navigate(user?.access_role === "Staff" ? "/me" : "/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not update password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2">
          <BrandLogo size={40} />
          <p className="font-heading text-lg font-bold">NestVillage Care Metrics</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-6 text-amber-900" data-testid="forced-banner">
          <div className="flex items-start gap-2">
            <ShieldAlert size={18} className="text-amber-700 mt-0.5" />
            <div>
              <p className="text-sm font-semibold">First-time login</p>
              <p className="text-xs mt-0.5 leading-relaxed">For your security, please set a permanent password before continuing.</p>
            </div>
          </div>
        </div>
        <h2 className="text-h1">Change your password</h2>
        <p className="text-sm text-slate-500 mt-1.5">Hello {user?.full_name?.split(" ")[0]}, choose a strong password you'll remember.</p>
        <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="forced-change-form">
          <div>
            <label className="text-small-caps">Temporary password</label>
            <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400"
              data-testid="forced-current" />
          </div>
          <div>
            <label className="text-small-caps">New password</label>
            <input type="password" required minLength={6} value={pw1} onChange={(e) => setPw1(e.target.value)} autoComplete="new-password"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400"
              data-testid="forced-pw1" />
          </div>
          <div>
            <label className="text-small-caps">Confirm new password</label>
            <input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400"
              data-testid="forced-pw2" />
          </div>
          {error && <p className="text-xs text-rose-600 font-medium" data-testid="forced-error">{error}</p>}
          <button type="submit" disabled={submitting}
            className="w-full px-4 py-2.5 rounded-lg bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold disabled:opacity-60"
            data-testid="forced-submit-btn">
            {submitting ? "Updating…" : "Update password & continue"}
          </button>
          <button type="button" onClick={logout}
            className="w-full px-4 py-2 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-600 text-sm font-medium"
            data-testid="forced-cancel-btn">
            Sign out
          </button>
        </form>
      </div>
    </div>
  );
}
